import axios from "axios";
import { toast } from "react-toastify";
import { hasPermission } from "../../../utils/permissions";
import {
  Cpu, Plus, Pencil, Trash2,
  ChevronLeft, ChevronRight, PackageOpen,
} from "lucide-react";
import PageSizeSelect from "../../../components/ui/PageSizeSelect";

const Componentes = ({
  accesoriosInfo = [],
  page,
  setPage,
  pageSize,
  setPageSize,
  totalPages,
  loading = false,
  token,
  API,
  fetchAccesorios,
  setShowAccesorioModal,
  setSelectedAccesorioId,
}) => {
  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este accesorio?")) return;
    try {
      if (!API) throw new Error("API no definida");
      if (!id) throw new Error("ID de accesorio no definido");
      await axios.delete(`${API}/accesorios/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.info("Accesorio eliminado correctamente");
      fetchAccesorios();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Error al eliminar accesorio";
      toast.error(msg);
    }
  };

  const start = (page - 1) * pageSize;
  const paged = Array.isArray(accesoriosInfo)
    ? accesoriosInfo.slice(start, start + pageSize)
    : [];

  const canEdit = hasPermission("equipos.editar");

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <Cpu className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              Componentes y accesorios
            </h3>
            <p className="text-xs text-gray-400">
              {accesoriosInfo.length} componente{accesoriosInfo.length !== 1 ? "s" : ""} registrado{accesoriosInfo.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {canEdit && (
          <button
            onClick={() => {
              setSelectedAccesorioId(null);
              setShowAccesorioModal(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Agregar
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div className="flex items-center justify-end mb-2">
        <PageSizeSelect
          value={pageSize}
          onChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Tipo
              </th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Descripción
              </th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Serial
              </th>
              {canEdit && (
                <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 bg-white">
            {loading ? (
              <tr>
                <td colSpan={canEdit ? 4 : 3} className="py-12 text-center text-sm text-gray-400">
                  Cargando…
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 4 : 3} className="py-12 text-center">
                  <PackageOpen className="h-9 w-9 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Sin componentes registrados</p>
                </td>
              </tr>
            ) : (
              paged.map((item) => (
                <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800">
                    {item.tipoAccesorio || "—"}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">
                    {item.descripcion || "—"}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500 font-mono">
                    {item.serial || "—"}
                  </td>
                  {canEdit && (
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          onClick={() => {
                            setSelectedAccesorioId(item._id);
                            setShowAccesorioModal(true);
                          }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          {paged.length === 0
            ? "Sin resultados"
            : `${(page - 1) * pageSize + 1}–${Math.min(
                page * pageSize,
                accesoriosInfo.length,
              )} de ${accesoriosInfo.length}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-2 text-sm tabular-nums">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Componentes;
